import { api, ApiError } from '../api';
import { can, fieldVisible, type Me } from '../auth/me';
import { PACKLISTS_RESOURCE, type PacklistView } from '../packlists/types';
import { finishSeries, type RepeatDraft } from './RepeatForm';

/** The repeats asked for in the form, made when the new event's card is left (with the packlist as it was filled). */
export type PendingSeries = { draft: RepeatDraft; firstDay: string; key: string };

/**
 * A "Nowe wydarzenie" / "Nowe wypożyczenie" made in the side panel (owner 2026-10-21 23:31): the card opens on `tab`
 * with `notice`. What the old last step did at its end waits until the card is left (closed, another card, another
 * screen): the series (so the copies take the packlist filled meanwhile) and, while the packlist is still empty, the
 * reminder "Uzupełnij sprzętówkę" (P15's "Dodam później" — closing without gear is "later").
 */
export type CreatedInPanel = {
  eventId: string;
  notice: string;
  tab: 'packlist' | 'details';
  series: PendingSeries | null;
  /** Leave the reminder when the card is left with an empty packlist ("Bez sprzętu": no). */
  remind: boolean;
};

const REMINDERS_RESOURCE = 'packlists.reminders';

/** Whether the person may leave "Uzupełnij sprzętówkę" (the reminder's text and day are written). */
export const mayRemind = (me: Me): boolean => can(me, REMINDERS_RESOURCE, 'create') && fieldVisible(me, REMINDERS_RESOURCE, 'text') && fieldVisible(me, REMINDERS_RESOURCE, 'dueDay');

/** '2026-10-21' → "21.10". */
const dayText = (day: string) => `${Number(day.slice(8, 10))}.${day.slice(5, 7)}`;

/** The line said once the card is left with an empty packlist and the reminder is set. */
export function laterText(dueDay: string | null, isOwner: boolean): string {
  return `Sprzętówka jest pusta — przypomnienie „Uzupełnij sprzętówkę”${dueDay ? ` przyjdzie ${dayText(dueDay)}` : ''} (dzwonek: Ty${isOwner ? '' : ' i właściciel'}).`;
}

/** Does what waits for leaving the new event's card; returns what to say (may be ''). Never throws. */
export async function settleCreated(created: CreatedInPanel, me: Me): Promise<string> {
  const said: string[] = [];
  // only whoever may read the packlist can tell it is empty; a refused read (scope "own") is "skip", not "nie zapisano"
  if (created.remind && mayRemind(me) && can(me, PACKLISTS_RESOURCE, 'view')) {
    try {
      const list = await api<PacklistView>(`/api/packlists/${created.eventId}`).catch((error: unknown) => {
        if (error instanceof ApiError && error.status === 403) return null;
        throw error;
      });
      if (list && list.lines.length === 0) {
        const r = await api<{ dueDay: string | null }>(`/api/packlists/${created.eventId}/fill-later`, { method: 'POST' });
        said.push(laterText(r.dueDay, me.isOwner));
      }
    } catch (error) {
      said.push(`Przypomnienia „Uzupełnij sprzętówkę” nie zapisano (${error instanceof ApiError ? error.message : 'błąd połączenia'}).`);
    }
  }
  if (created.series) said.push(await finishSeries(created.eventId, created.series.draft, created.series.firstDay, created.series.key));
  return said.filter(Boolean).join(' ');
}
